import 'react-quill-new/dist/quill.snow.css'
import './styles.scss'

import { Box } from '@mui/material'
import { CloseIcon } from 'assets/icons'
import { BlockOverlayProps } from 'components/BlockOverlay'
import IconButton from 'components/buttons/IconButton'
import NotesList from 'components/lists/NotesList'
import { MiscText } from 'components/login/styled'
import PopupSection from 'components/popup/PopupSection'
import { PopupSectionRow } from 'components/popup/PopupSectionRow'
import FilePickerProvider from 'components/providers/FilePickProvider'
import { TextWithLinks } from 'components/TextWithLinks'
import { IEventDetails } from 'hooks/useEventDetails'
import { t } from 'i18next'
import { Controller } from 'react-hook-form'
import ReactQuill from 'react-quill-new'
import Logger from 'services/Logger'
import { DocType, EventDoc, TaskType } from 'types/api/changes'
import { Model } from 'types/model'
import { convertBytesToMB } from 'utils/file'

import NoteDetailsPopup from '../noteDetails/NoteDetailsPopup'
import NoteTitleInput from './NoteTitleInput'

export interface NotesSectionProps extends BlockOverlayProps {
  isCurrentGroupIsGoogleCalendar: boolean
  eventHook: IEventDetails
  event: Model<EventDoc> | EventDoc
  onApproveNoteText: (s: string) => void
  openNoteDetails: boolean
  setOpenNoteDetails: (b: boolean) => void
}

export default function NotesSection(props: NotesSectionProps) {
  const {
    isCurrentGroupIsGoogleCalendar,
    eventHook,
    event,
    onApproveNoteText
  } = props

  Logger.blue('Notes:', isCurrentGroupIsGoogleCalendar)
  Logger.blue('Notes:', eventHook.getValues('Type'))
  Logger.blue('CustomURL:', eventHook.customUrl)
  Logger.blue('Attachments:', eventHook.attachements)

  const notesValue = eventHook.getValues('Notes')
  const notesWithoutCoverImage = Array.isArray(notesValue)
    ? notesValue.filter((note) => note.NoteType !== '1')
    : []
  const hasPersonalEventNoteWithoutCoverImage =
    eventHook.getValues('TaskType') === TaskType.personalEvent &&
    notesWithoutCoverImage.length > 0

  return (
    <PopupSection
      title={`${
        event.Type === DocType.NOTE
          ? t('noteContent')
          : `${t(isCurrentGroupIsGoogleCalendar ? 'notes' : 'notesSubtask')}`
      }`}
      isUiBlocked={props.isUiBlocked}
      blockedOverlayClick={props.blockedOverlayClick}
      contentZIndex={3}
      sectionId="notes-section"
      titlePadding={'0px'}
      noBottomBorder
    >
      {eventHook.getValues('Type') === DocType.NOTE ? (
        <Controller
          name="Notes"
          control={eventHook.control}
          defaultValue={event.Notes}
          render={({ field }) =>
            field.value ? (
              <FilePickerProvider>
                <NoteDetailsPopup
                  event={event}
                  eventHook={eventHook}
                  open={props.openNoteDetails}
                  setOpen={props.setOpenNoteDetails}
                  note={field.value?.[0]}
                >
                  <div
                    style={{
                      cursor: 'pointer',
                      maxHeight: '140px',
                      overflow: 'auto'
                    }}
                    onClick={() => {
                      props.setOpenNoteDetails(true)
                    }}
                  >
                    <ReactQuill
                      className="tfm-note-preview"
                      style={{ pointerEvents: 'none' }}
                      theme="snow"
                      modules={{ toolbar: false }}
                      value={
                        field.value?.[0]?.Note ??
                        `<p>${t('addNoteContent')}</p>`
                      }
                      readOnly
                    />
                  </div>
                </NoteDetailsPopup>
              </FilePickerProvider>
            ) : (
              <></>
            )
          }
        />
      ) : (
        <>
          {eventHook.notes ? (
            <Box id={'notes-list-container'}>
              <Controller
                name="Notes"
                control={eventHook.control}
                defaultValue={event.Notes}
                render={({ field }) =>
                  field.value ? (
                    <NotesList
                      isUiBlocked={props.isUiBlocked}
                      notes={field.value}
                      checkable={
                        eventHook.getValues('TaskType') !==
                          TaskType.personalEvent && !props.isUiBlocked
                      }
                      disableDelete={props.isUiBlocked}
                      setNotes={eventHook.setNotes}
                      event={props.event}
                    />
                  ) : (
                    <></>
                  )
                }
              />
              {eventHook.customUrl && (
                <PopupSectionRow
                  onlyTitle
                  title={<TextWithLinks text={`URL: ${eventHook.customUrl}`} />}
                  value={
                    <IconButton
                      size="xs"
                      onClick={() => {
                        if (eventHook.customUrl)
                          eventHook.setCustomUrl(undefined)
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  }
                />
              )}
              {eventHook.attachements &&
                eventHook.attachements?.map((attachment, index) => {
                  return (
                    <PopupSectionRow
                      key={index}
                      onlyTitle
                      title={
                        <Box display={'flex'} flexDirection={'column'}>
                          <TextWithLinks
                            title={attachment.FileName}
                            text={attachment.Url}
                          />
                          <MiscText fontSize={'12px'}>{`${convertBytesToMB(
                            attachment.FileSize
                          )} MB`}</MiscText>
                        </Box>
                      }
                      value={
                        <IconButton
                          size="xs"
                          onClick={() => {
                            if (eventHook.attachements)
                              eventHook.setAttachments(
                                eventHook.attachements.filter(
                                  (item) => item !== attachment
                                )
                              )
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      }
                    />
                  )
                })}
            </Box>
          ) : (
            <></>
          )}
          {props.isUiBlocked || hasPersonalEventNoteWithoutCoverImage ? (
            <></>
          ) : (
            <NoteTitleInput
              eventHook={eventHook}
              docType={eventHook.getValues('Type')}
              taskType={eventHook.getValues('TaskType')}
              isCurrentGroupIsGoogleCalendar={isCurrentGroupIsGoogleCalendar}
              onApproveNoteText={onApproveNoteText}
              onAttachFile={(text, bytes) => {
                eventHook.addNote(text, bytes)
              }}
            />
          )}
        </>
      )}
    </PopupSection>
  )
}
